import { assign, sendParent, setup } from 'xstate';
import type { FilingDomainEvent } from '@/features/filing/state/events';
import type { ApprovalEvent } from './events';
import { recordApprovalActor } from './actors';

type ApprovalContext = {
  pending: { approvalId: string; filingId: string } | null;
  error: unknown;
};

type ApprovalEvents = ApprovalEvent | FilingDomainEvent;

const approvalSetup = setup({
  types: {
    context: {} as ApprovalContext,
    events: {} as ApprovalEvents,
  },
  actors: { recordApprovalActor },
  actions: {
    prepareApproval: assign({
      pending: ({ event }) =>
        event.type === 'approval.record' ? { approvalId: crypto.randomUUID(), filingId: event.filingId } : null,
      error: () => null,
    }),
    reportError: assign({ error: ({ event }) => event }),
    emitRecorded: sendParent(({ context }) => ({
      type: 'approval.recorded',
      approvalId: context.pending?.approvalId ?? '',
      filingId: context.pending?.filingId ?? '',
    })),
  },
});

export const approvalMachine = approvalSetup.createMachine({
  id: 'approval',
  initial: 'idle',
  context: { pending: null, error: null },
  on: {
    'approval.record': { target: '.recording', actions: 'prepareApproval' },
    'filing.corrected': { target: '.idle' },
    'filing.rejected-corrected': { target: '.idle' },
  },
  states: {
    idle: {},
    recording: {
      invoke: {
        src: 'recordApprovalActor',
        input: ({ context }) => context.pending ?? { approvalId: crypto.randomUUID(), filingId: '' },
        onDone: {
          target: 'idle',
          actions: [
            'emitRecorded',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Approval recorded', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'error',
          actions: [
            'reportError',
            sendParent({
              type: 'ui.toast.push',
              toast: {
                title: 'Approval could not be recorded',
                description: 'Review the filing and try again.',
                tone: 'error',
              },
            }),
          ],
        },
      },
    },
    error: {
      on: { 'approval.record': { target: 'recording', actions: 'prepareApproval' } },
    },
  },
});
